import React, { useState } from 'react';

const ExportAnalyticsButton = ({
  activeTab,
  startDate,
  endDate,
  endpoint = '/api/users/analytics/export-excel',
  typeParam = 'userType',
  filePrefix,
  label = 'Export to Excel',
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  // Replace process.env with a fallback for demo purposes
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const resolvedFilePrefix =
    filePrefix || (activeTab === "scanners" ? "scanner_operators" : "indexers");
  const handleExport = async () => {
    setIsExporting(true);
    setExportError(null);

    try {
      // Prepare query parameters
      const params = {
        [typeParam]: activeTab === "scanners" ? "scanner" : "indexer"
      };

      // Add date filters if provided
      if (startDate && endDate) {
        params.startDate = startDate.toISOString().split('T')[0];
        params.endDate = endDate.toISOString().split('T')[0];
      }

      // Build query string
      const queryString = new URLSearchParams(params).toString();

      const response = await fetch(`${baseUrl}${endpoint}?${queryString}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${document.cookie.split('token=')[1]?.split(';')[0]}`,
        },
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      // Create blob and download file
      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename
      const dateStr = new Date().toISOString().split('T')[0];
      link.download = `${resolvedFilePrefix}_analytics_${dateStr}.xlsx`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Export error:', error);
      setExportError(
        `Failed to export ${activeTab === "scanners" ? "scanner operators" : "indexers"} analytics`
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
      <button
        onClick={handleExport}
        disabled={isExporting}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 16px',
          backgroundColor: isExporting ? '#f3f4f6' : '#10B981',
          color: isExporting ? '#6b7280' : '#ffffff',
          border: 'none',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '500',
          cursor: isExporting ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        }}
        onMouseEnter={(e) => {
          if (!isExporting) {
            e.target.style.backgroundColor = '#059669';
            e.target.style.transform = 'translateY(-1px)';
            e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isExporting) {
            e.target.style.backgroundColor = '#10B981';
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
          }
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            animation: isExporting ? 'spin 1s linear infinite' : 'none'
          }}
        >
          {isExporting ? (
            // Loading spinner
            <circle cx="12" cy="12" r="3"/>
          ) : (
            // Download icon
            <>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7,10 12,15 17,10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </>
          )}
        </svg>
        {isExporting ? 'Exporting...' : label}
      </button>
      
      {exportError && (
        <div style={{
          fontSize: '12px',
          color: '#ef4444',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '4px',
          padding: '6px 8px',
          maxWidth: '200px',
          textAlign: 'center'
        }}>
          {exportError}
        </div>
      )}
      
      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ExportAnalyticsButton;